import { getIframeBaseUrl } from "@/utils/platform";
import { StorageService } from "@/services/storageService";

/**
 * Security configuration for postMessage communication.
 */
export const SECURITY_CONFIG = {
    // We'll dynamically determine the allowed origin for our service iframes.
    getAllowedOrigins: async () => {
        const iframeBaseUrl = await getIframeBaseUrl();
        try {
            const url = new URL(iframeBaseUrl);
            const origins = [url.origin];
            
            // For local development via Traefik (see README.md)
            if (import.meta.env.VITE_ENV === 'LOCAL') {
                origins.push("http://authheila.com");
            }
            
            return origins;
        } catch (e) {
            console.error("Failed to parse iframe base URL for security config", e);
            return [];
        }
    },

    // Actions that access sensitive native APIs or perform critical app state changes.
    PRIVILEGED_ACTIONS: [
        'initBackgroundGeolocation',
        'startBackgroundGeolocation',
        'stopBackgroundGeolocation',
        'sendApplicationLog',
        'switchToSettings',
        'sendLocationLog',
        'windowOpen'
    ]
};

/**
 * Validates if the message origin is in the allowlist.
 */
export const validateOrigin = async (origin: string): Promise<boolean> => {
    const allowedOrigins = await SECURITY_CONFIG.getAllowedOrigins();
    // Also allow the app's own origin (for local testing/web version)
    const currentOrigin = window.location.origin;
    const isAllowed = allowedOrigins.includes(origin) || origin === currentOrigin;
    
    if (!isAllowed) {
        console.warn(`[Security] Blocked message from untrusted origin: ${origin}`);
    }
    return isAllowed;
};

/**
 * Basic schema validation for incoming messages.
 */
export interface HeilaMessage {
    type: 'HEILA_ACTION';
    action: string;
    data: any;
    nonce: string;
}

export const isValidHeilaMessage = (data: any): data is HeilaMessage => {
    if (!data || typeof data !== 'object') return false;
    if (data.type !== 'HEILA_ACTION') return false;
    if (typeof data.action !== 'string') return false;
    if (typeof data.nonce !== 'string') return false;
    return true;
};

/**
 * Simple authorization check for privileged actions.
 */
export const isAuthorizedAction = (action: string): boolean => {
    // If it's a privileged action, log it and return true (for now).
    // In a more robust system, we would check user permissions here.
    if (SECURITY_CONFIG.PRIVILEGED_ACTIONS.includes(action)) {
        console.info(`[Security] Privileged action detected: ${action}. Processing with authorization.`);
        return true;
    }
    
    // If the action is neither privileged nor explicitly allowed by the bridge logic,
    // it will still be rejected in IframeBridge.handle if not found in registries.
    return true;
};

/**
 * Handshake implementation (Requirement 7)
 * Nonces are now stored in a map keyed by serviceId to prevent multiple iframes 
 * from overwriting each other's security tokens.
 */
export const generateHandshakeNonce = async (serviceId: string): Promise<string> => {
    const nonce = Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    
    // Get existing nonces or start fresh
    let stored = await StorageService.Get<any>("HANDSHAKE_NONCE");
    
    // Ensure we have a valid object to work with
    if (!stored || typeof stored !== 'object') {
        stored = {};
    }
    
    // Update map for this specific service
    stored[serviceId] = nonce;
    
    await StorageService.Set("HANDSHAKE_NONCE", stored);
    return nonce;
};

export const validateHandshakeNonce = async (nonce: string, serviceId: string): Promise<boolean> => {
    const stored = await StorageService.Get<Record<string, string>>("HANDSHAKE_NONCE");
    if (!stored || typeof stored !== 'object') return false;
    
    const expectedNonce = stored[serviceId];
    return !!expectedNonce && nonce === expectedNonce;
};
